import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useParams, useNavigate } from "react-router-dom";
import { Menu, X, BarChart3 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const Header = () => {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const base = `/${lang}`;

  const navItems = [
    { key: "about", href: `${base}#about`, testId: "header-nav-about" },
    { key: "how", href: `${base}#how`, testId: "header-nav-how-it-works" },
    { key: "advantages", href: `${base}#advantages`, testId: "header-nav-advantages" },
    { key: "faq", href: `${base}#faq`, testId: "header-nav-faq" },
  ];

  const switchLang = (target) => {
    // Preserve the current route type but switch language segment (without triggering scroll)
    const path = location.pathname;
    let newPath = `/${target}`;
    if (path.startsWith("/ec/")) newPath = path.replace(/^\/ec/, `/${target}`);
    else if (path.startsWith("/en/")) newPath = path.replace(/^\/en/, `/${target}`);
    else if (path === "/ec" || path === "/en") newPath = `/${target}`;
    navigate(newPath, { state: { skipScroll: true } });
  };

  const handleNav = (e, href) => {
    if (href.includes("#")) {
      e.preventDefault();
      const [pathPart, hash] = href.split("#");
      if (location.pathname === pathPart) {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ block: "start" });
      } else {
        navigate(pathPart);
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ block: "start" });
        }, 50);
      }
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-slate-900 text-white border-b border-slate-800 shadow-md"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link
          to={base}
          data-testid="header-logo"
          className="flex items-center gap-2 group"
        >
          <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-emerald-500/15 border border-emerald-500/30">
            <BarChart3 className="w-4 h-4 text-emerald-400" strokeWidth={2.4} />
          </span>
          <span className="font-display text-xl font-bold tracking-tight">
            {t("brand.name")}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.key}
              href={item.href}
              onClick={(e) => handleNav(e, item.href)}
              data-testid={item.testId}
              className="text-sm text-slate-200 hover:text-emerald-400 font-medium"
            >
              {t(`nav.${item.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-1 border border-slate-700 rounded-full p-1 bg-slate-800/60">
            <button
              onClick={() => switchLang("ec")}
              data-testid="header-lang-switch-es"
              className={`px-3 py-1 text-xs font-mono rounded-full ${
                lang === "ec" ? "bg-emerald-500 text-slate-900" : "text-slate-300 hover:text-white"
              }`}
              aria-label={t("language.es_full")}
            >
              {t("language.es")}
            </button>
            <button
              onClick={() => switchLang("en")}
              data-testid="header-lang-switch-en"
              className={`px-3 py-1 text-xs font-mono rounded-full ${
                lang === "en" ? "bg-emerald-500 text-slate-900" : "text-slate-300 hover:text-white"
              }`}
              aria-label={t("language.en_full")}
            >
              {t("language.en")}
            </button>
          </div>

          <a
            href={`${base}#lead-form`}
            onClick={(e) => handleNav(e, `${base}#lead-form`)}
            data-testid="header-cta-button"
            className="hidden lg:inline-flex items-center px-4 py-2 rounded-full bg-emerald-500 text-slate-900 text-sm font-semibold hover:bg-emerald-400"
          >
            {t("nav.cta")}
          </a>

          <button
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-700 text-slate-200"
            onClick={() => setOpen((s) => !s)}
            data-testid="header-mobile-hamburger-button"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-slate-900 border-t border-slate-800" data-testid="header-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => handleNav(e, item.href)}
                data-testid={`${item.testId}-mobile`}
                className="text-sm text-slate-200 hover:text-emerald-400 py-2 border-b border-slate-800"
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2 sm:hidden">
              <button
                onClick={() => switchLang("ec")}
                data-testid="header-lang-switch-es-mobile"
                className={`flex-1 py-2 text-xs font-mono rounded-md border ${
                  lang === "ec"
                    ? "bg-emerald-500 text-slate-900 border-emerald-500"
                    : "border-slate-700 text-slate-300"
                }`}
              >
                {t("language.es_full")}
              </button>
              <button
                onClick={() => switchLang("en")}
                data-testid="header-lang-switch-en-mobile"
                className={`flex-1 py-2 text-xs font-mono rounded-md border ${
                  lang === "en"
                    ? "bg-emerald-500 text-slate-900 border-emerald-500"
                    : "border-slate-700 text-slate-300"
                }`}
              >
                {t("language.en_full")}
              </button>
            </div>
            <a
              href={`${base}#lead-form`}
              onClick={(e) => handleNav(e, `${base}#lead-form`)}
              data-testid="header-cta-button-mobile"
              className="mt-2 inline-flex justify-center items-center px-4 py-3 rounded-md bg-emerald-500 text-slate-900 text-sm font-semibold"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
